import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  NgZone,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import Tooltip from 'ol-ext/overlay/Tooltip.js';
import { Coordinate, createStringXY } from 'ol/coordinate';
import { Extent, getHeight, getTopLeft, getWidth } from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON';
import Draw, { DrawEvent } from 'ol/interaction/Draw';
import OLMap from 'ol/Map';
import { get as GetProjection, Projection } from 'ol/proj';
import { register } from 'ol/proj/proj4';
import { OSM, Vector, XYZ } from 'ol/source.js';
import View from 'ol/View';
import proj4 from 'proj4';
import { environment } from 'src/environments/environment';

import TileGrid from 'ol/tilegrid/TileGrid';

import ControlLegend from 'ol-ext/control/Legend';
import Item from 'ol-ext/legend/Item';
import Legend from 'ol-ext/legend/Legend';
import FillPattern from 'ol-ext/style/FillPattern';
import { MousePosition, ScaleLine } from 'ol/control';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style, { StyleFunction } from 'ol/style/Style';
import Text from 'ol/style/Text';
import { FlcBaseLayer } from '../layerswitcher/flc-base-layer.model';
import { LayerSwitcherService } from '../layerswitcher/layerswitcher.service';

import MVT from 'ol/format/MVT';
import TileLayer from 'ol/layer/Tile';
import VectorTileLayer from 'ol/layer/VectorTile';
import VectorTileSource from 'ol/source/VectorTile';
import { SideNavService } from 'src/app/core-flc/sidenav/sidenav.service';
import { FlcControlLayer } from '../layerswitcher/flc-control-layer.model';

import { Location } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Feature } from 'ol';
import { EventsKey } from 'ol/events';
import { Point } from 'ol/geom';
import BaseLayer from 'ol/layer/Base';
import Layer from 'ol/layer/Layer';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Icon from 'ol/style/Icon';
import { Subscription } from 'rxjs';
import { DialogRasterComponent } from 'src/app/flc/raster/dialog-raster/dialog-raster.component';
import { DialogStressAreaComponent } from 'src/app/flc/stress-area/dialog-stress-area/dialog-stress-area.component';
import { DialogStressGroupComponent } from 'src/app/flc/stress-group/dialog-stress-group/dialog-stress-group.component';
import {
  CalculationStatusType,
  TunnelStatusType,
} from 'src/app/server-api/models';
import { RasterCatCode } from 'src/app/server-api/models/raster-cat-code';
import {
  LogMessageControllerService,
  RasterControllerService,
  SysParamControllerService,
  TunnelSectionControllerService,
} from 'src/app/server-api/services';

@Component({
  selector: 'app-map',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.css'],
})
export class MapComponent implements AfterViewInit, OnDestroy {
  map: OLMap;
  projection: Projection;
  view: View;
  maxExtent: Extent;
  eventsKeys: EventsKey[] = [];

  selectedSection: string;
  routePath: string;
  currentGroup: string = environment.default_stress_group;

  titleLegend: '';
  stressStyleFunction: StyleFunction;
  stressTransStyleFunction: StyleFunction;
  stressAreaLegend: Legend;

  stressSpecsStyleFunction: StyleFunction;
  stressSpecsAreaLegend: Legend;

  stressAvgStyleFunction: StyleFunction;
  stressAreaAvgLegend: Legend;

  sectionStyleFunction: StyleFunction;
  sectionLegend: Legend;

  legendCtrl: ControlLegend;

  drawLine: Draw;
  tooltip: any;
  measureActive: boolean = false;

  @ViewChild('rasterLegend', { static: false }) rasterLegend: ElementRef;

  selectedBaseLayerSubcription: Subscription;
  layerChangedSubscription: Subscription;
  selectedSectionLayerSubscription: Subscription;
  selectedLogMessageLayerSubscription: Subscription;
  removeFeatureLayerSubscription: Subscription;
  setSelectedMtvSubscription: Subscription;
  resizeMapSubscription: Subscription;
  addRasterLayerSubscription: Subscription;
  addSectionLayerSubscription: Subscription;

  setVisibleSectionParentSubscription: Subscription;
  setvisibleRasterParentSubscription: Subscription;

  center: Coordinate = environment.default_center;

  toKeepLayers: string[] = [];
  controlLayersMap: Map<string, TileLayer<XYZ>> = new Map<
    string,
    TileLayer<XYZ>
  >();
  flcControlLayers: FlcControlLayer[] = [];
  baseLayers: TileLayer<any>[] = [];
  mtvLayers: Map<string, VectorTileLayer> = new Map<string, VectorTileLayer>();
  selectedMtvId: string;
  sectionLayer: VectorTileLayer;
  logMessageLayer: BaseLayer;
  clickPointLayer: BaseLayer;
  clickMarker: Feature;
  stressLayer: VectorTileLayer;
  stressLayerTrans: VectorTileLayer;
  stressSpecsLayer: VectorTileLayer;
  stressAvgLayer: VectorTileLayer;
  stressGroupLayer: VectorTileLayer;
  rasterLayer: TileLayer<XYZ>;
  rasterLayerId: string;
  detailSectionId: string;

  //in systemparams use double " instead of single ' to be a valid json
  stressTypeColors: { [key: string]: { [key: string]: string } };
  stressSpecsTypeColors: { [key: string]: { [key: string]: string } };
  stressAvgTypeColors: { [key: string]: { [key: string]: string } };

  //in systemparams use double " instead of single ' to be a valid json
  tunnelSectionColors: { [key: string]: { [key: string]: string } };

  referenceLineColor: string;

  tunnelSectionSelected: string = 'red';
  logMessageSelected: string = environment.draw_color;

  colorsFromSysParams: boolean = false;

  topLegend: Legend;

  featureLayer: any;

  //system-params stored in a map
  private systemParamsMap: Map<String, String>;

  @Input() mapContainerClass = 'map-container';
  @Input() target = 'map';
  @Input() zoom: number = environment.default_zoom;
  @Output() mapReady = new EventEmitter<OLMap>();
  @Input() colorMap: string;
  @Input() dataPopup: boolean = false;
  @Input() rastersOnMap: boolean = false;
  @Input() stressAreasOnMap: boolean = false;
  @Input() currentCategory: RasterCatCode;
  @Input() currentCategoryName: string = '';
  @Input() lsService: LayerSwitcherService;
  @Input() rasterUpload: boolean = false;

  currentCategoryColorMap: string = '';

  public dialog = inject(MatDialog);

  constructor(
    public location: Location,
    private zone: NgZone,
    private rasterService: RasterControllerService,
    private sidenavService: SideNavService,
    private tsService: TunnelSectionControllerService,
    private spService: SysParamControllerService,
    private lmService: LogMessageControllerService,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService
  ) {}

  ngOnDestroy(): void {
    if (this.selectedBaseLayerSubcription)
      this.selectedBaseLayerSubcription.unsubscribe();
    if (this.layerChangedSubscription)
      this.layerChangedSubscription.unsubscribe();
    if (this.selectedSectionLayerSubscription)
      this.selectedSectionLayerSubscription.unsubscribe();
    if (this.selectedLogMessageLayerSubscription)
      this.selectedLogMessageLayerSubscription.unsubscribe();
    if (this.removeFeatureLayerSubscription)
      this.removeFeatureLayerSubscription.unsubscribe();
    if (this.setSelectedMtvSubscription)
      this.setSelectedMtvSubscription.unsubscribe();
    if (this.resizeMapSubscription) this.resizeMapSubscription.unsubscribe();
    if (this.addRasterLayerSubscription)
      this.addRasterLayerSubscription.unsubscribe();
    if (this.addSectionLayerSubscription)
      this.addSectionLayerSubscription.unsubscribe();
    if (this.map) this.map.setTarget(null);
  }

  ngAfterViewInit(): void {
    this.translate
      .get(['map_button.layer_legend'])
      .subscribe((translations: string) => {
        this.titleLegend = translations['map_button.layer_legend'];
      });

    this.route.url.subscribe((event) => {
      event[0] ? (this.routePath = event[0].path) : '';
    });

    if (!this.map) {
      this.zone.runOutsideAngular(() => this.initMap());
    }

    setTimeout(() => {
      this.mapReady.emit(this.map);

      if (this.stressAreasOnMap) {
        this.addStressLayersOnMap();
      }
      if (this.rastersOnMap) {
        this.addRasterLayersOnMap();
      }

      this.addClickHandlers();
    }, 200);
  }

  ngOnChanges(changes: SimpleChanges) {}

  private initMap(): void {
    proj4.defs(
      'EPSG:5598',
      '+proj=tmerc +lat_0=0 +lon_0=11.33333333333333 +k=1 +x_0=1000000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +vunits=m +no_defs'
    );

    register(proj4);

    this.projection = GetProjection('EPSG:5598');
    this.projection.setExtent(environment.maxExtent);

    this.view = new View({
      center: this.center,
      zoom: this.zoom,
      maxZoom: 14,
      projection: this.projection,
      extent: this.maxExtent,
      showFullExtent: true,
      constrainResolution: true,
    });

    this.map = new OLMap({
      layers: [],
      target: 'map',
      view: this.view,
    });

    if (this.map) {
      this.map.setTarget(this.target);
    }

    let osm = new TileLayer({
      source: new OSM({
        opaque: false,
        url: environment.osm_url,
      }),
    });
    osm.set('title', 'OSM');

    this.baseLayers.push(osm);

    this.map.addLayer(osm);

    this.setControls();
    this.setMeasure();

    this.addClickPositionLayer();
    this.addSubscriptionsGlobal();
    this.addSubscriptionsLayers();
    this.addSubscriptionRasters();
    this.addSubscriptionSections();
    this.addSubscriptionLogMessages();
    this.addSubscriptionLegend();
    this.addSubscriptionMeasure();
  }

  showPopupDepth(ids: string[], x: number, y: number) {
    let popupDialog = this.dialog.open(DialogRasterComponent, {
      data: {
        id: 'dummy',
        x: x,
        y: y,
        rasterIds: JSON.stringify(ids),
        buttonCancel: 'Close',
      },
      hasBackdrop: false,
    });

    this.router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) {
        if (popupDialog && !e['url'].includes(this.routePath + '/'))
          popupDialog.close();
      }
    });

    popupDialog.afterClosed().subscribe((result) => {
      popupDialog = null;
    });
  }

  setControls() {
    var mousePosition = new MousePosition({
      coordinateFormat: createStringXY(4),
      projection: 'EPSG:5598',
      className: 'custom-mouse-position',
    });
    this.map.addControl(mousePosition);

    var scaleLine = new ScaleLine({
      units: 'metric',
      steps: 4,
    });

    this.map.addControl(scaleLine);
  }

  setMeasure() {
    this.drawLine = new Draw({
      type: 'LineString',
    });
    this.map.addInteraction(this.drawLine);

    this.drawLine.setActive(false);
    this.measureActive = false;

    this.tooltip = new Tooltip();
    this.tooltip.getElement().style.fontSize = '20px';
    this.map.addOverlay(this.tooltip);

    this.eventsKeys.push(
      this.drawLine.on('drawstart', (e) => {
        e.preventDefault();
        this.switchMetric('meter');
        this.tooltip.setFeature(e.feature);
      })
    );

    this.eventsKeys.push(
      this.drawLine.on('change:active', (e) => {
        e.preventDefault();
        this.tooltip.removeFeature((e as unknown as DrawEvent).feature);
      })
    );

    this.eventsKeys.push(
      this.drawLine.on('drawend', (e) => {
        e.preventDefault();
        this.tooltip.removeFeature((e as unknown as DrawEvent).feature);
      })
    );
  }

  switchMetric(metric: string) {
    if (this.tooltip) {
      this.tooltip.formatLength = (length: number) => {
        let m: string = '';
        if (metric === 'km') m = `${(length / 1000).toFixed(2)} km`;
        else m = `${length.toFixed(2)} m`;
        return m;
      };
    }
  }

  addClickPositionLayer() {
    this.clickMarker = new Feature({
      geometry: new Point(this.center),
    });

    this.clickPointLayer = new VectorLayer({
      source: new Vector({ features: [] }),
      declutter: true,
      style: new Style({
        image: new Icon({
          src: 'assets/click_marker.svg',
          opacity: 1,
          scale: 1,
          color: environment.main_color,
        }),
        fill: new Fill({
          color: 'transparent',
        }),
      }),
    });

    this.clickPointLayer.setZIndex(1000015);
    this.map.addLayer(this.clickPointLayer);
  }

  addSubscriptionsGlobal() {
    this.resizeMapSubscription = this.lsService.resizeMap.subscribe(() => {
      if (this.map) {
        this.lsService.toCloseLayerSwitcherOverlay();
        this.map.updateSize();
      }
    });
  }

  addSubscriptionsLayers() {
    this.selectedBaseLayerSubcription =
      this.lsService.setSelectedBaseLayer.subscribe((bl: FlcBaseLayer) => {
        this.baseLayers.forEach((layer) => {
          this.map.removeLayer(layer);
        });
        this.baseLayers.forEach((layer) => {
          if (bl.title == layer.get('title')) {
            this.map.addLayer(layer);
          }
        });
      });
  }

  addSubscriptionRasters() {
    this.addRasterLayerSubscription =
      this.lsService.addRasterTiffLayer.subscribe((rasterId) => {
        this.rasterLayer = null;
        this.rasterLayerId = '';
        this.addRasterTiff(rasterId, null);

        switch (this.target) {
          case 'raster-map':
            this.fitMapToRaster(rasterId);
            break;
          default:
            break;
        }
      });

    this.layerChangedSubscription = this.lsService.layerchanged.subscribe(
      (layer) => {
        if (this.clickPointLayer instanceof VectorLayer)
          this.clickPointLayer.getSource().removeFeature(this.clickMarker);
        if (layer.type === 'STRESS') {
          this.stressLayer.setOpacity(layer.opacity);
          this.stressLayer.setVisible(layer.visible);
        } else if (layer.type === 'STRESS_TRANS') {
          this.stressLayerTrans.setOpacity(layer.opacity);
          this.stressLayerTrans.setVisible(layer.visible);
        } else if (layer.type === 'STRESS_SPECS') {
          this.stressSpecsLayer.setOpacity(layer.opacity);
          this.stressSpecsLayer.setVisible(layer.visible);
        } else if (layer.type === 'STRESS_AVG') {
          this.stressAvgLayer.setOpacity(layer.opacity);
          this.stressAvgLayer.setVisible(layer.visible);
        } else if (layer.type === 'GROUP') {
          if (
            layer.selectedDropdown &&
            this.currentGroup !== layer.selectedDropdown
          ) {
            this.currentGroup = layer.selectedDropdown;
            this.stressGroupLayer
              .getSource()
              .setUrl(
                this.detailSectionId
                  ? environment.api_base +
                      environment.section_stress_group_tile_url
                        .replace(':id', this.detailSectionId)
                        .replace(':grouptype', layer.selectedDropdown)
                  : environment.api_base +
                      environment.section_stress_group_tiles_url.replace(
                        ':grouptype',
                        layer.selectedDropdown
                      )
              );
            this.stressGroupLayer.getSource().refresh();
          }
          this.stressGroupLayer.setOpacity(layer.opacity);
          this.stressGroupLayer.setVisible(layer.visible);
        } else {
          let visiblePresent = false;
          this.controlLayersMap.forEach(
            (tileLayer: TileLayer<XYZ>, name: string) => {
              if (name === layer.id) {
                tileLayer.setOpacity(layer.opacity);
                tileLayer.setVisible(layer.visible);
                if (layer.visible && layer.showLegend) {
                  this.colorMap = layer.colorMap;
                }
              }
              visiblePresent = tileLayer.isVisible() ? true : visiblePresent;
            }
          );
          if (!visiblePresent) this.colorMap = this.currentCategoryColorMap;
        }

        this.map.updateSize();
      }
    );

    this.setvisibleRasterParentSubscription =
      this.lsService.setVisibleParentLayer.subscribe((visible) => {
        if (this.rasterLayer) {
          let props = this.rasterLayer.getSource().getProperties();
          props['visible'] = visible;
          this.rasterLayer.setProperties(props);
        }
      });
  }

  addRasterTiff(id: string, layer: FlcControlLayer) {
    let pngTileLayer = new TileLayer({
      source: new XYZ({
        projection: this.projection,
        url:
          id === null
            ? environment.api_base + environment.raster_tiles_url
            : environment.api_base +
              environment.raster_tile_url.replace(':id', id),
        crossOrigin: '',
        gutter: 0,
        maxZoom: 14,
        wrapX: false,
        opaque: false,
      }),
      visible: layer ? layer.visible : true,
      opacity: layer ? layer.opacity : 1,
    });

    if (layer) {
      this.controlLayersMap.set(layer.id, pngTileLayer);
    } else {
      this.rasterLayer = pngTileLayer;
      this.rasterLayerId = id;
    }

    pngTileLayer.setZIndex(1000011);

    this.map.addLayer(pngTileLayer);
  }

  //add subscription for tunnel section layer
  addSubscriptionSections() {
    this.selectedSection = null;

    //select tunnelsection based on selected in table
    this.selectedSectionLayerSubscription =
      this.lsService.setSelectedSection.subscribe((sectionId) => {
        this.selectedSection = sectionId;
        this.sectionLayer.getSource().refresh();
        if (this.stressLayer) this.stressLayer.getSource().refresh();
        if (this.stressLayerTrans) this.stressLayerTrans.getSource().refresh();
        if (this.stressAvgLayer) this.stressAvgLayer.getSource().refresh();
        if (this.stressSpecsLayer) this.stressSpecsLayer.getSource().refresh();
        if (this.stressGroupLayer) this.stressGroupLayer.getSource().refresh();
        if (sectionId) this.fitMapToSection(sectionId);
      });

    //add tunnel sections layers, all features at ones
    this.addSectionLayerSubscription =
      this.lsService.addNewSectionLayer.subscribe((id) => {
        this.detailSectionId = id;
        if (!this.colorsFromSysParams) {
          this.spService.getSysParams({ size: 9999 }).subscribe(
            (params) => {
              this.systemParamsMap = new Map(
                params['content'].map((i) => [i.key, i.value])
              );
              let saColorMap = this.systemParamsMap.get('STRESS_AREA_COLORMAP');
              let saAvgColorMap = this.systemParamsMap.get(
                'STRESS_AREA_AVG_COLORMAP'
              );
              let saSpecsColorMap = this.systemParamsMap.get(
                'STRESS_AREA_SPECS_COLORMAP'
              );
              let tsColorMap = this.systemParamsMap.get(
                'TUNNEL_SECTION_COLORMAP'
              );
              let tsColorSelected = this.systemParamsMap.get(
                'TUNNEL_SECTION_COLOR_SELECTED'
              );
              this.referenceLineColor = this.systemParamsMap.get(
                'REFERENCE_LINE_COLOR'
              )
                ? this.systemParamsMap.get('REFERENCE_LINE_COLOR').toString()
                : '#FF00FF';

              if (saColorMap) {
                this.stressTypeColors = JSON.parse(
                  saColorMap.replaceAll('\n', '').replaceAll(' ', '')
                );
                //check if colors present for every state
                for (let key in CalculationStatusType) {
                  if (!this.stressTypeColors[CalculationStatusType[key]]) {
                    window.alert(
                      "There is no color defined for calculation status '" +
                        CalculationStatusType[key] +
                        "', please contact the administrator! Default colors are used."
                    );
                  }
                }
              }

              if (saAvgColorMap) {
                this.stressAvgTypeColors = JSON.parse(
                  saAvgColorMap.replaceAll('\n', '').replaceAll(' ', '')
                );
              }

              if (saSpecsColorMap) {
                this.stressSpecsTypeColors = JSON.parse(
                  saSpecsColorMap.replaceAll('\n', '').replaceAll(' ', '')
                );
              }

              if (tsColorMap) {
                this.tunnelSectionColors = JSON.parse(
                  tsColorMap.replaceAll('\n', '').replaceAll(' ', '')
                );
                //check if colors present for every state
                for (let key in TunnelStatusType) {
                  if (!this.tunnelSectionColors[TunnelStatusType[key]]) {
                    window.alert(
                      "There is no color defined for tunnel section status '" +
                        TunnelStatusType[key] +
                        "', please contact the administrator! Default colors are used."
                    );
                  }
                }
              }

              if (tsColorSelected && tsColorSelected.length > 2) {
                this.tunnelSectionSelected = tsColorSelected.valueOf();
              }
            },
            (error) => {
              console.error('error login: ' + JSON.stringify(error));
            },
            () => {
              this.colorsFromSysParams = true;
              this.finishTunnelLayerOnMap(id);
            }
          );
        } else this.finishTunnelLayerOnMap(id);

        //redraw stress area is already on the map
        if (this.stressLayer) this.stressLayer.getSource().refresh();
        if (this.stressLayerTrans) this.stressLayerTrans.getSource().refresh();
        if (this.stressAvgLayer) this.stressAvgLayer.getSource().refresh();
        if (this.stressSpecsLayer) this.stressSpecsLayer.getSource().refresh();

        //redraw stress group is already on the map
        if (this.stressGroupLayer) this.stressGroupLayer.getSource().refresh();
      });

    this.setVisibleSectionParentSubscription =
      this.lsService.setVisibleParentLayer.subscribe((visible) => {
        if (this.sectionLayer) {
          let props = this.sectionLayer.getSource().getProperties();
          props['visible'] = visible;
          this.sectionLayer.setProperties(props);
        }
      });
  }

  finishTunnelLayerOnMap(id) {
    let ndvi = {
      stroke: new Stroke({
        color: '#1E90FF',
        width: 2,
      }),
      fill: new Fill({
        color: 'rgba(30,144,255, 0.13)',
      }),
    };

    this.sectionStyleFunction = (feature) => {
      var sectionStyle = new Style(ndvi);
      let f_class = feature.getProperties()['class'];
      if (f_class == 'boundary') {
        let status = feature.getProperties()['tunnel_status'];
        //for (const [key, value] of Object.entries(feature.getProperties())) {
        //   console.log(`${key}: ${value}`);
        // }

        let color = '#1E90FF';
        let fill = 'rgba(30,144,255, 0.13)';
        let width = '3';
        if (status && this.tunnelSectionColors[status]) {
          color = this.tunnelSectionColors[status]['stroke'];
          fill = this.tunnelSectionColors[status]['fill'];
          width = this.tunnelSectionColors[status]['width'];
        }

        sectionStyle.getStroke().setColor(color);
        sectionStyle.getFill().setColor(fill);
        sectionStyle.getStroke().setWidth(Number(width));

        if (this.selectedSection && feature) {
          if (feature.getProperties()['id'] === this.selectedSection) {
            sectionStyle.getStroke().setColor(this.tunnelSectionSelected);
          }
        }
      } else if (f_class == 'refLine') {
        sectionStyle.getStroke().setColor(this.referenceLineColor);
        sectionStyle.getStroke().setWidth(2);
        sectionStyle.getStroke().setLineDash([5, 5]);
      } else if (f_class == 'gravelArea') {
        sectionStyle.getFill().setColor('rgba(80,40,40, 0.13)');
        sectionStyle.getStroke().setColor('rgba(10,10,10,0.2)');
        sectionStyle.getStroke().setWidth(0.1);
        sectionStyle.getStroke().setLineDash([]);
      }

      return sectionStyle;
    };

    this.selectedSection = null;

    const extent = this.projection.getExtent();
    const startResolution = Math.max(getWidth(extent), getHeight(extent)) / 256;
    const resolutions = new Array(16);

    for (let i = 0, ii = resolutions.length; i < ii; ++i) {
      resolutions[i] = startResolution / Math.pow(2, i);
    }

    if (this.sectionLayer) this.map.removeLayer(this.sectionLayer);
    this.sectionLayer = new VectorTileLayer({
      declutter: true,
      source: new VectorTileSource({
        projection: 'EPSG:5598',
        maxZoom: 15,
        tileGrid: new TileGrid({
          extent: extent,
          origin: getTopLeft(extent),
          resolutions: resolutions,
        }),
        format: new MVT({}),
        url: id
          ? environment.api_base +
            environment.section_tunnel_tile_url.replace(':id', id)
          : environment.api_base + environment.section_tunnel_tiles_url,
      }),

      style: this.sectionStyleFunction,
    });

    this.sectionLayer.setZIndex(1000016);

    this.map.addLayer(this.sectionLayer);

    if (this.target === 'section-map') this.fitMapToSection(id);

    // Define a new legend
    this.sectionLegend = new Legend({
      title: 'Tunnel section',
      style: this.sectionStyleFunction,
      margin: 5,
      textStyle: new Text({
        font: '12px sans-serif',
      }),
      titleStyle: new Text({
        font: 'bold 12px sans-serif',
        textAlign: 'start',
        justify: 'left',
      }),
    });

    this.configLegendCtrl();

    this.topLegend.addItem(this.sectionLegend);

    for (let key in TunnelStatusType) {
      if (this.tunnelSectionColors[TunnelStatusType[key]]) {
        this.sectionLegend.addItem(
          new Item({
            title: TunnelStatusType[key],
            properties: {
              class: 'boundary',
              tunnel_status: TunnelStatusType[key],
            },
            typeGeom: 'Polygon',
            width: 25,
            heigth: 25,
            margin: 3,
          })
        );
      }
    }
    this.sectionLegend.addItem(
      new Item({
        title: 'Reference line                                ',
        properties: { class: 'refLine' },
        typeGeom: 'LineString',
        width: 25,
        margin: 3,
      })
    );
  }

  configLegendCtrl() {
    if (!this.legendCtrl) {
      this.topLegend = new Legend({
        title: '',
      });

      this.legendCtrl = new ControlLegend({
        legend: this.topLegend,
        collapsed: true,
      });

      this.map.addControl(this.legendCtrl);
    }
  }

  showPopupStress(id: string, type: string) {
    let popupDialog;
    if (type === 'STRESS' || type === 'STRESS_AVG' || type === 'STRESS_SPECS') {
      popupDialog = this.dialog.open(DialogStressAreaComponent, {
        data: { id: id, buttonCancel: 'Close' },
        hasBackdrop: false,
      });
    } else {
      popupDialog = this.dialog.open(DialogStressGroupComponent, {
        data: { id: id, buttonCancel: 'Close' },
        hasBackdrop: false,
      });
    }

    this.router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) {
        if (popupDialog && !e['url'].includes(this.routePath + '/'))
          popupDialog.close();
      }
    });

    popupDialog.afterClosed().subscribe((result) => {
      popupDialog = null;
    });
  }

  fitMapToSection(id: string) {
    //zoom in to extent tunnelsection
    this.tsService.getTunnelSection({ id: id }).subscribe((ts) => {
      if (ts && ts.surface) {
        let f = new GeoJSON().readGeometry(ts.surface, {
          dataProjection: 'EPSG:5598',
          featureProjection: 'EPSG:5598',
        });
        this.map.getView().fit(f.getExtent(), { maxZoom: 8 });
      }
    });
  }

  fitMapToRaster(id: string) {
    //zoom in to extent tunnelsection
    this.rasterService.getRaster({ id: id }).subscribe((raster) => {
      if (raster && raster.outline) {
        let f = new GeoJSON().readGeometry(raster.outline, {
          dataProjection: 'EPSG:5598',
          featureProjection: 'EPSG:5598',
        });

        this.map.getView().fit(f.getExtent(), { maxZoom: 8 });
      }
    });
  }

  addRasterLayersOnMap() {
    this.rasterService.getRasters({ size: 9999 }).subscribe(
      (value) => {
        let rasterLayers = value['content'];

        this.flcControlLayers.forEach((f, i) => {
          if (f.type === 'RASTER') this.flcControlLayers.splice(i, 1);
        });

        rasterLayers.forEach((raster) => {
          if (
            (!raster.temporary && this.rasterUpload) ||
            (!raster.temporary &&
              !this.rasterUpload &&
              raster.category.name !== this.currentCategoryName)
          ) {
            let r = {} as FlcControlLayer;
            r.id = raster.id;
            r.type = 'RASTER';
            r.name = raster.category.name;
            r.code = raster.category.code;
            r.colorMap = raster.category.colorMap;
            r.opacity = 0.5;
            r.zindex = 1;
            if (
              this.currentCategory &&
              raster.category.code === this.currentCategory
            ) {
              this.currentCategoryColorMap = r.colorMap;
              r.visible = false;
              r.showLegend = false;
            } else {
              r.visible = false;
              r.showLegend = false;
            }
            r.title = raster.category.name;
            this.flcControlLayers.push(r);

            this.addRasterTiff(raster.id, r);
          }
        });
        this.lsService.toSetFlcControlLayers(this.flcControlLayers);
      },
      (error) => {
        console.error(JSON.stringify(error));
        this.sidenavService.setLoading(false);
      },
      () => {
        this.sidenavService.setLoading(false);
      }
    );
  }

  addClickHandlers() {
    if (this.dataPopup) {
      this.map.on('singleclick', (e) => {
        //only popup from zoomlevel 7
        let first: boolean = true;
        if (!this.measureActive && this.map.getView().getZoom() > 6) {
          this.zone.run(() => {
            let rasterids: string[] = [];
            if (this.rasterLayer && this.rasterLayer.isVisible()) {
              if (!rasterids.includes(this.rasterLayerId))
                rasterids.push(this.rasterLayerId);
            }

            if (this.controlLayersMap && this.controlLayersMap.size > 0) {
              this.controlLayersMap.forEach(
                (tileLayer: TileLayer<XYZ>, name: string) => {
                  if (tileLayer.isVisible()) {
                    if (!rasterids.includes(name)) rasterids.push(name);
                  }
                }
              );
            }
            if (rasterids.length > 0) {
              if (first) {
                let coordinates: Coordinate = e.coordinate;
                this.clickMarker.setGeometry(new Point(coordinates));
                if (this.clickPointLayer instanceof VectorLayer) {
                  this.clickPointLayer
                    .getSource()
                    .removeFeature(this.clickMarker);
                  this.clickPointLayer.getSource().addFeature(this.clickMarker);
                }
                first = false;
              }
              this.showPopupDepth(rasterids, e.coordinate[0], e.coordinate[1]);
            }
          });
        }
      });
    }

    if (this.target === 'sections') {
      //only when coming from list sections
      this.map.on('singleclick', (e) => {
        this.map.forEachFeatureAtPixel(e.pixel, (feature, layer) => {
          this.zone.run(() => {
            if (layer === this.sectionLayer) {
              if (
                !this.selectedSection ||
                this.selectedSection !== feature.getProperties()['id']
              ) {
                this.selectedSection = feature.getProperties()['id'];
                this.lsService.mapSectionSelect(this.selectedSection);
                this.sectionLayer.changed();
              }
            }
          });
        });
      });
    }

    if (this.target === 'section-map' || this.target === 'sections') {
      if (this.dataPopup) {
        this.map.on('singleclick', (e) => {
          //only popup from zoomlevel 7
          if (this.map.getView().getZoom() > 6) {
            let first: boolean = true;
            this.map.forEachFeatureAtPixel(e.pixel, (feature, layer: Layer) => {
              this.zone.run(() => {
                if (first) {
                  let coordinates: Coordinate = e.coordinate;
                  this.clickMarker.setGeometry(new Point(coordinates));
                  if (this.clickPointLayer instanceof VectorLayer) {
                    this.clickPointLayer
                      .getSource()
                      .removeFeature(this.clickMarker);
                    this.clickPointLayer
                      .getSource()
                      .addFeature(this.clickMarker);
                  }
                  first = false;
                }
                if (!this.measureActive &&
                  (this.stressLayer.isVisible() &&
                  layer === this.stressLayer) || (this.stressLayerTrans.isVisible() && layer === this.stressLayerTrans) &&
                  feature.getProperties()['id']
                ) {
                  this.showPopupStress(feature.getProperties()['id'], 'STRESS');
                }
                if (!this.measureActive &&
                  this.stressSpecsLayer.isVisible() &&
                  layer === this.stressSpecsLayer &&
                  feature.getProperties()['id']
                ) {
                  this.showPopupStress(
                    feature.getProperties()['id'],
                    'STRESS_SPECS'
                  );
                }
                if (!this.measureActive &&
                  this.stressAvgLayer.isVisible() &&
                  !this.stressLayer.isVisible() && !this.stressLayerTrans.isVisible() &&
                  layer === this.stressAvgLayer &&
                  feature.getProperties()['id']
                ) {
                  this.showPopupStress(
                    feature.getProperties()['id'],
                    'STRESS_AVG'
                  );
                }
                if (!this.measureActive &&
                  this.stressGroupLayer.isVisible() &&
                  layer === this.stressGroupLayer &&
                  feature.getProperties()['id']
                ) {
                  this.showPopupStress(feature.getProperties()['id'], 'GROUP');
                }
              });
            });
          }
        });
      }
    }
  }

  addStressLayersOnMap() {
    if (!this.colorsFromSysParams) {
      this.spService.getSysParams({ size: 9999 }).subscribe(
        (params) => {
          this.systemParamsMap = new Map(
            params['content'].map((i) => [i.key, i.value])
          );
          let saColorMap = this.systemParamsMap.get('STRESS_AREA_COLORMAP');
          let saAvgColorMap = this.systemParamsMap.get(
            'STRESS_AREA_AVG_COLORMAP'
          );
          let tsColorMap = this.systemParamsMap.get('TUNNEL_SECTION_COLORMAP');
          let saSpecsColorMap = this.systemParamsMap.get(
            'STRESS_AREA_SPECS_COLORMAP'
          );
          let tsColorSelected = this.systemParamsMap.get(
            'TUNNEL_SECTION_COLOR_SELECTED'
          );

          if (saColorMap) {
            this.stressTypeColors = JSON.parse(
              saColorMap.replaceAll('\n', '').replaceAll(' ', '')
            );
          }

          if (saAvgColorMap) {
            this.stressAvgTypeColors = JSON.parse(
              saAvgColorMap.replaceAll('\n', '').replaceAll(' ', '')
            );
          }

          if (saSpecsColorMap) {
            this.stressSpecsTypeColors = JSON.parse(
              saSpecsColorMap.replaceAll('\n', '').replaceAll(' ', '')
            );
          }

          if (tsColorMap) {
            this.tunnelSectionColors = JSON.parse(
              tsColorMap.replaceAll('\n', '').replaceAll(' ', '')
            );
          }

          if (tsColorSelected && tsColorSelected.length > 2) {
            this.tunnelSectionSelected = tsColorSelected.valueOf();
          }
        },
        (error) => {
          console.error('error login: ' + JSON.stringify(error));
        },
        () => {
          this.colorsFromSysParams = true;
          this.finishStressLayer();
          this.finishStressLayerTrans();
          this.finishStressAvgLayer();
          this.finishStressSpecsLayer();
          this.finishStressGroupLayersOnMap();
        }
      );
    } else {
      this.finishStressLayer();
      this.finishStressLayerTrans();
      this.finishStressAvgLayer();
      this.finishStressSpecsLayer();
      this.finishStressGroupLayersOnMap();
    }
  }

  finishStressLayer() {
    let defaultColors = {
      fill: new Fill({
        color: 'yellow',
      }),
      stroke: new Stroke({
        color: 'grey',
        width: 1,
      }),
    };

    this.stressStyleFunction = (feature) => {
      let stressStyle = new Style(defaultColors);
      let status = feature.getProperties()['calc_status'];

      let color = 'grey';
      let fill = 'yellow';
      let width = '1';
      let pattern = null;

      if (status && this.stressTypeColors[status]) {
        color = this.stressTypeColors[status]['stroke'];
        fill = this.stressTypeColors[status]['fill'];
        width = this.stressTypeColors[status]['width'];
        if (this.stressTypeColors[status]['pattern']) {
          pattern = this.stressTypeColors[status]['pattern'];
        }
      }

      if (pattern) {
        let fillPattern = new FillPattern({
          color: color,
          pattern: pattern,
          size: 2,
          spacing: 5,
          angle: 0,
          offset: 0,
          scale: 2,
          fill: new Fill({ color: fill }),
        });
        stressStyle.setFill(fillPattern);
      } else {
        stressStyle.getFill().setColor(fill);
      }
      stressStyle.getStroke().setColor(color);
      stressStyle.getStroke().setWidth(Number(width));

      return stressStyle;
    };

    const extent = this.projection.getExtent();
    const startResolution = Math.max(getWidth(extent), getHeight(extent)) / 256;
    const resolutions = new Array(16);

    for (let i = 0, ii = resolutions.length; i < ii; ++i) {
      resolutions[i] = startResolution / Math.pow(2, i);
    }

    if (this.stressLayer) this.map.removeLayer(this.stressLayer);
    this.stressLayer = new VectorTileLayer({
      declutter: true,
      source: new VectorTileSource({
        projection: 'EPSG:5598',
        maxZoom: 15,
        tileGrid: new TileGrid({
          extent: extent,
          origin: getTopLeft(extent),
          resolutions: resolutions,
        }),
        format: new MVT({}),
        url: this.detailSectionId
          ? environment.api_base +
            environment.section_stress_tile_url.replace(
              ':id',
              this.detailSectionId
            )
          : environment.api_base + environment.section_stress_tiles_url,
      }),
      opacity: 0.8,
      visible: false,

      style: this.stressStyleFunction,
    });

    // Define a new legend
    this.stressAreaLegend = new Legend({
      title: 'Error overview',
      titleStyle: new Text({
        font: 'bold 12px sans-serif',
        textAlign: 'left',
        justify: 'left',
      }),
      style: this.stressStyleFunction,
      margin: 5,
      textStyle: new Text({
        font: '12px sans-serif',
      }),
    });

    this.configLegendCtrl();

    this.topLegend.addItem(this.stressAreaLegend);

    for (let key in CalculationStatusType) {
      if (this.stressTypeColors[CalculationStatusType[key]]) {
        this.stressAreaLegend.addItem(
          new Item({
            title: CalculationStatusType[key],
            properties: { calc_status: CalculationStatusType[key] },
            typeGeom: 'Polygon',
            width: 25,
            heigth: 25,
            margin: 3,
          })
        );
      }
    }

    let r = {} as FlcControlLayer;
    r.id = 'STRESS';
    r.type = 'STRESS';
    r.name = 'Stress areas';
    r.code = '';
    r.colorMap = '';
    r.opacity = 0.8;
    r.zindex = 1;
    r.visible = false;
    r.showLegend = false;

    r.title = 'Error Overview All';
    this.flcControlLayers.push(r);

    this.stressLayer.setZIndex(1000012);

    this.map.addLayer(this.stressLayer);
  }

  finishStressLayerTrans() {
    let defaultColors = {
      fill: new Fill({
        color: 'yellow',
      }),
      stroke: new Stroke({
        color: 'grey',
        width: 1,
      }),
    };

    this.stressTransStyleFunction = (feature) => {
      let stressStyle = new Style(defaultColors);
      let status = feature.getProperties()['calc_status_trans'];

      let color = 'grey';
      let fill = 'yellow';
      let width = '1';
      let pattern = null;

      if (status && this.stressTypeColors[status]) {
        color = this.stressTypeColors[status]['stroke'];
        fill = this.stressTypeColors[status]['fill'];
        width = this.stressTypeColors[status]['width'];
        if (this.stressTypeColors[status]['pattern']) {
          pattern = this.stressTypeColors[status]['pattern'];
        }
      }

      if (pattern) {
        let fillPattern = new FillPattern({
          color: color,
          pattern: pattern,
          size: 2,
          spacing: 5,
          angle: 0,
          offset: 0,
          scale: 2,
          fill: new Fill({ color: fill }),
        });
        stressStyle.setFill(fillPattern);
      } else {
        stressStyle.getFill().setColor(fill);
      }
      stressStyle.getStroke().setColor(color);
      stressStyle.getStroke().setWidth(Number(width));

      return stressStyle;
    };

    const extent = this.projection.getExtent();
    const startResolution = Math.max(getWidth(extent), getHeight(extent)) / 256;
    const resolutions = new Array(16);

    for (let i = 0, ii = resolutions.length; i < ii; ++i) {
      resolutions[i] = startResolution / Math.pow(2, i);
    }

    if (this.stressLayerTrans) this.map.removeLayer(this.stressLayerTrans);
    this.stressLayerTrans = new VectorTileLayer({
      declutter: true,
      source: new VectorTileSource({
        projection: 'EPSG:5598',
        maxZoom: 15,
        tileGrid: new TileGrid({
          extent: extent,
          origin: getTopLeft(extent),
          resolutions: resolutions,
        }),
        format: new MVT({}),
        url: this.detailSectionId
          ? environment.api_base +
            environment.section_stress_tile_url.replace(
              ':id',
              this.detailSectionId
            )
          : environment.api_base + environment.section_stress_tiles_url,
      }),
      opacity: 0.8,
      visible: false,

      style: this.stressTransStyleFunction,
    });

    // no new legend, the same as stress area

    let r = {} as FlcControlLayer;
    r.id = 'STRESS_TRANS';
    r.type = 'STRESS_TRANS';
    r.name = 'Stress areas trans';
    r.code = '';
    r.colorMap = '';
    r.opacity = 0.8;
    r.zindex = 1;
    r.visible = false;
    r.showLegend = false;

    r.title = 'Error Overview (Trans)';
    this.flcControlLayers.push(r);

    this.stressLayerTrans.setZIndex(1000013);

    this.map.addLayer(this.stressLayerTrans);
  }

  finishStressSpecsLayer() {
    let defaultColors = {
      fill: new Fill({
        color: 'yellow',
      }),
      stroke: new Stroke({
        color: 'grey',
        width: 1,
      }),
    };

    this.stressSpecsStyleFunction = (feature, resolution) => {
      let stressStyle = new Style(defaultColors);

      let perc: number = feature.getProperties()['specs_perc'] as number;
      let showLabel: boolean = true;
      if (feature.getProperties()['label'] !== undefined)
        showLabel = feature.getProperties()['label'] as boolean;

      let color = 'grey';
      let fill = 'yellow';
      let width = '1';
      let pattern = null;

      let found: boolean = false;

      Object.entries(this.stressSpecsTypeColors).forEach(([key, value]) => {
        if (!found && perc < parseFloat(key)) {
          found = true;
          color = this.stressSpecsTypeColors[key]['stroke'];
          fill = this.stressSpecsTypeColors[key]['fill'];
          width = this.stressSpecsTypeColors[key]['width'];

          if (this.stressSpecsTypeColors[key]['pattern']) {
            pattern = this.stressSpecsTypeColors[key]['pattern'];
          }

          if (pattern) {
            let fillPattern = new FillPattern({
              color: color,
              pattern: pattern,
              size: 2,
              spacing: 5,
              angle: 0,
              offset: 0,
              scale: 2,
              fill: new Fill({ color: fill }),
            });
            stressStyle.setFill(fillPattern);
          } else {
            stressStyle.getFill().setColor(fill);
          }

          stressStyle.getStroke().setColor(color);
          stressStyle.getStroke().setWidth(Number(width));
        }
      });

      if (showLabel) {
        let fontSize = 12;
        if (this.map.getView().getZoom() === 10) fontSize = 12;
        else if (this.map.getView().getZoom() === 11) fontSize = 20;
        else if (this.map.getView().getZoom() >= 12) fontSize = 24;

        stressStyle.setText(
          new Text({
            font: fontSize + 'px Calibri,sans-serif',
            text: perc.toFixed(3),
            declutterMode: 'declutter',
          })
        );
      }

      return stressStyle;
    };

    const extent = this.projection.getExtent();
    const startResolution = Math.max(getWidth(extent), getHeight(extent)) / 256;
    const resolutions = new Array(16);

    for (let i = 0, ii = resolutions.length; i < ii; ++i) {
      resolutions[i] = startResolution / Math.pow(2, i);
    }

    if (this.stressSpecsLayer) this.map.removeLayer(this.stressSpecsLayer);
    this.stressSpecsLayer = new VectorTileLayer({
      declutter: true,
      source: new VectorTileSource({
        projection: 'EPSG:5598',
        maxZoom: 15,
        tileGrid: new TileGrid({
          extent: extent,
          origin: getTopLeft(extent),
          resolutions: resolutions,
        }),
        format: new MVT({}),
        url: this.detailSectionId
          ? environment.api_base +
            environment.section_stress_tile_url.replace(
              ':id',
              this.detailSectionId
            )
          : environment.api_base + environment.section_stress_tiles_url,
      }),
      opacity: 0.8,
      visible: false,

      style: this.stressSpecsStyleFunction,
    });

    // Define a new legend
    this.stressSpecsAreaLegend = new Legend({
      title: 'Unity Check',
      titleStyle: new Text({
        font: 'bold 12px sans-serif',
        textAlign: 'left',
        justify: 'left',
      }),
      style: this.stressSpecsStyleFunction,
      margin: 5,
      textStyle: new Text({
        font: '12px sans-serif',
      }),
    });

    this.configLegendCtrl();

    this.topLegend.addItem(this.stressSpecsAreaLegend);

    Object.entries(this.stressSpecsTypeColors).forEach(([key, value]) => {
      this.stressSpecsAreaLegend.addItem(
        new Item({
          title: '< ' + key,
          properties: { specs_perc: +key - 0.01, label: false },
          typeGeom: 'Polygon',
          width: 25,
          heigth: 25,
          margin: 3,
        })
      );
    });

    let a = {} as FlcControlLayer;
    a.id = 'STRESS_SPECS';
    a.type = 'STRESS_SPECS';
    a.name = 'Stress areas specs';
    a.code = '';
    a.colorMap = '';
    a.opacity = 0.8;
    a.zindex = 1;
    a.visible = false;
    a.showLegend = false;

    a.title = 'Unity Check';
    this.flcControlLayers.push(a);

    this.stressSpecsLayer.setZIndex(1000013);

    this.map.addLayer(this.stressSpecsLayer);
  }

  finishStressAvgLayer() {
    let defaultColors = {
      fill: new Fill({
        color: 'yellow',
      }),
      stroke: new Stroke({
        color: 'grey',
        width: 1,
      }),
    };

    this.stressAvgStyleFunction = (feature, resolution) => {
      let stressStyle = new Style(defaultColors);

      let avg: number = feature.getProperties()['stress_pattern_avg'] as number;
      let showLabel: boolean = true;
      if (feature.getProperties()['label'] !== undefined)
        showLabel = feature.getProperties()['label'] as boolean;

      let color = 'grey';
      let fill = 'yellow';
      let width = '1';
      let pattern = null;

      let found: boolean = false;

      Object.entries(this.stressAvgTypeColors).forEach(([key, value]) => {
        if (!found && avg < parseFloat(key)) {
          found = true;
          color = this.stressAvgTypeColors[key]['stroke'];
          fill = this.stressAvgTypeColors[key]['fill'];
          width = this.stressAvgTypeColors[key]['width'];

          if (this.stressAvgTypeColors[key]['pattern']) {
            pattern = this.stressAvgTypeColors[key]['pattern'];
          }

          if (pattern) {
            let fillPattern = new FillPattern({
              color: color,
              pattern: pattern,
              size: 2,
              spacing: 5,
              angle: 0,
              offset: 0,
              scale: 2,
              fill: new Fill({ color: fill }),
            });
            stressStyle.setFill(fillPattern);
          } else {
            stressStyle.getFill().setColor(fill);
          }

          stressStyle.getStroke().setColor(color);
          stressStyle.getStroke().setWidth(Number(width));
        }
      });

      if (showLabel) {
        let fontSize = 12;
        if (this.map.getView().getZoom() === 10) fontSize = 12;
        else if (this.map.getView().getZoom() === 11) fontSize = 20;
        else if (this.map.getView().getZoom() >= 12) fontSize = 24;

        stressStyle.setText(
          new Text({
            font: fontSize + 'px Calibri,sans-serif',
            text: avg.toFixed(3),
            declutterMode: 'declutter',
          })
        );
      }

      return stressStyle;
    };

    const extent = this.projection.getExtent();
    const startResolution = Math.max(getWidth(extent), getHeight(extent)) / 256;
    const resolutions = new Array(16);

    for (let i = 0, ii = resolutions.length; i < ii; ++i) {
      resolutions[i] = startResolution / Math.pow(2, i);
    }

    if (this.stressAvgLayer) this.map.removeLayer(this.stressAvgLayer);
    this.stressAvgLayer = new VectorTileLayer({
      declutter: true,
      source: new VectorTileSource({
        projection: 'EPSG:5598',
        maxZoom: 15,
        tileGrid: new TileGrid({
          extent: extent,
          origin: getTopLeft(extent),
          resolutions: resolutions,
        }),
        format: new MVT({}),
        url: this.detailSectionId
          ? environment.api_base +
            environment.section_stress_tile_url.replace(
              ':id',
              this.detailSectionId
            )
          : environment.api_base + environment.section_stress_tiles_url,
      }),
      opacity: 0.8,
      visible: false,

      style: this.stressAvgStyleFunction,
    });

    // Define a new legend
    this.stressAreaAvgLegend = new Legend({
      title: 'Stress area average',
      titleStyle: new Text({
        font: 'bold 12px sans-serif',
        textAlign: 'left',
        justify: 'left',
      }),
      style: this.stressAvgStyleFunction,
      margin: 5,
      textStyle: new Text({
        font: '12px sans-serif',
      }),
    });

    this.configLegendCtrl();

    this.topLegend.addItem(this.stressAreaAvgLegend);

    Object.entries(this.stressAvgTypeColors).forEach(([key, value]) => {
      this.stressAreaAvgLegend.addItem(
        new Item({
          title: '< ' + key,
          properties: { stress_pattern_avg: +key - 0.01, label: false },
          typeGeom: 'Polygon',
          width: 25,
          heigth: 25,
          margin: 3,
        })
      );
    });

    let a = {} as FlcControlLayer;
    a.id = 'STRESS_AVG';
    a.type = 'STRESS_AVG';
    a.name = 'Stress areas average';
    a.code = '';
    a.colorMap = '';
    a.opacity = 0.8;
    a.zindex = 1;
    a.visible = false;
    a.showLegend = false;

    a.title = 'Stress areas average';
    this.flcControlLayers.push(a);

    this.stressAvgLayer.setZIndex(1000013);

    this.map.addLayer(this.stressAvgLayer);
  }

  finishStressGroupLayersOnMap() {
    const extent = this.projection.getExtent();
    const startResolution = Math.max(getWidth(extent), getHeight(extent)) / 256;
    const resolutions = new Array(16);

    for (let i = 0, ii = resolutions.length; i < ii; ++i) {
      resolutions[i] = startResolution / Math.pow(2, i);
    }

    if (this.stressGroupLayer) this.map.removeLayer(this.stressGroupLayer);
    this.stressGroupLayer = new VectorTileLayer({
      declutter: true,
      source: new VectorTileSource({
        projection: 'EPSG:5598',
        maxZoom: 15,
        tileGrid: new TileGrid({
          extent: extent,
          origin: getTopLeft(extent),
          resolutions: resolutions,
        }),
        format: new MVT({}),
        url: this.detailSectionId
          ? environment.api_base +
            environment.section_stress_group_tile_url
              .replace(':id', this.detailSectionId)
              .replace(':grouptype', environment.default_stress_group)
          : environment.api_base +
            environment.section_stress_group_tiles_url.replace(
              ':grouptype',
              environment.default_stress_group
            ),
      }),
      opacity: 0.8,
      visible: false,

      style: this.stressStyleFunction,
    });

    let r = {} as FlcControlLayer;
    r.id = 'GROUP';
    r.type = 'GROUP';
    r.name = 'Stress groups';
    r.code = '';
    r.colorMap = '';
    r.opacity = 0.8;
    r.zindex = 1;
    r.visible = false;
    r.showLegend = false;
    r.showDropDown = true;

    r.title = 'Stress groups';
    this.flcControlLayers.push(r);

    this.stressGroupLayer.setZIndex(1000013);

    this.map.addLayer(this.stressGroupLayer);
  }

  addSubscriptionLogMessages() {
    //select tunnelsection based on selected in table
    this.selectedLogMessageLayerSubscription =
      this.lsService.setLogMessage.subscribe((logMessageId) => {
        if (this.logMessageLayer) this.map.removeLayer(this.logMessageLayer);

        //todo set in system params
        const styles = [
          new Style({
            stroke: new Stroke({
              color: this.logMessageSelected,
              width: 3,
              lineDash: [0.1, 5],
            }),
          }),
        ];

        if (logMessageId) {
          this.lmService.getLogMessage({ id: logMessageId }).subscribe((lm) => {
            if (lm && lm.area) {
              let f = new GeoJSON().readGeometry(lm.area, {
                dataProjection: 'EPSG:5598',
                featureProjection: 'EPSG:5598',
              });

              this.logMessageLayer = new VectorLayer({
                declutter: true,
                source: new VectorSource({
                  features: new GeoJSON().readFeatures(lm.area),
                }),
                style: styles,
              });

              this.logMessageLayer.setZIndex(1000017);

              this.map.addLayer(this.logMessageLayer);

              this.map.getView().fit(f.getExtent(), { maxZoom: 8 });
            }
          });
        }
      });
  }

  addSubscriptionLegend() {
    this.lsService.setVisibleLegends.subscribe((visible) => {
      if (this.legendCtrl)
        visible ? this.legendCtrl.show() : this.legendCtrl.hide();
    });
  }

  addSubscriptionMeasure() {
    this.lsService.setVisibleMeasure.subscribe((visible) => {
      this.drawLine.setActive(visible);
      this.measureActive = visible;
    });
  }
}
